/**
 * Shared role presentation (icon + accent + copy) for the role-selection screen
 * and the login screen. Keeping it in one place means the card a user taps on the
 * Landing page and the header they see on the login page always read with the same identity.
 *
 * Every role has an entry here so the login header can greet it, but only admin and
 * support are listed on the Landing page. Patient, doctor and pharmacist sign in at
 * their own URL (/patient, /doctor, /pharmacist) — see roleLogin() in AuthContext.
 */

import type { ReactNode } from "react";

import type { Role } from "./AuthContext";
import { ChatIcon, PillIcon, ShieldIcon, StethoscopeIcon, UserIcon } from "./roleIcons";

export interface RolePresentation {
  role: Role;
  title: string;
  description: string;
  /**
   * Tailwind utility group for the role's icon chip. Each role keeps its own
   * hue for recognition, but the tints are drawn from the shared brand family
   * (see src/index.css) so the set reads as one system.
   */
  accent: string; // icon chip background + text
  icon: ReactNode;
}

export const ROLE_LIST: RolePresentation[] = [
  {
    role: "patient",
    title: "Patient",
    description: "View prescriptions, place orders, and manage your family profiles.",
    accent: "bg-accent-50 text-accent-700 group-hover:bg-accent-100",
    icon: <UserIcon />,
  },
  {
    role: "doctor",
    title: "Doctor",
    description: "Create and review prescriptions for your patients.",
    accent: "bg-brand-50 text-brand group-hover:bg-brand-100",
    icon: <StethoscopeIcon />,
  },
  {
    role: "pharmacist",
    title: "Pharmacist",
    description: "Work the dispensing queue and digitise uploaded prescriptions.",
    accent: "bg-accent-100 text-accent-800 group-hover:bg-accent-200",
    icon: <PillIcon />,
  },
  {
    role: "admin",
    title: "Admin",
    description: "Manage medicines, staff, refunds, and pharmacy settings.",
    accent: "bg-brand-100 text-brand-800 group-hover:bg-brand-200",
    icon: <ShieldIcon />,
  },
  {
    role: "support",
    title: "Support",
    description: "Respond to enquiries and track request analytics.",
    accent: "bg-warning-soft text-warning group-hover:brightness-95",
    icon: <ChatIcon />,
  },
];

const ROLE_META: Record<Role, RolePresentation> = ROLE_LIST.reduce(
  (acc, item) => {
    acc[item.role] = item;
    return acc;
  },
  {} as Record<Role, RolePresentation>,
);

/** Look up a role's presentation; returns undefined for an unknown/missing role. */
export function rolePresentation(role: string | null | undefined): RolePresentation | undefined {
  return role ? ROLE_META[role as Role] : undefined;
}
